// packages/components/src/showcase/DemoEmbedSimple.tsx
import React from 'react';
import { Card } from '@dopomogai/ui';
import { PlayCircle } from 'lucide-react';

type Props = {
  title?: string;
  videoUrl: string; // YouTube/Vimeo/Loom URL or a direct .mp4/.webm
  description?: string;
  className?: string;
};

function toEmbedUrl(url: string): { type: 'youtube' | 'vimeo' | 'loom' | 'file'; src: string } {
  const u = url.toLowerCase();

  // YouTube link variants
  if (u.includes('youtube.com/watch') || u.includes('youtu.be/')) {
    let id = '';
    if (u.includes('youtu.be/')) id = u.split('youtu.be/')[1].split(/[?&]/)[0];
    else {
      const params = new URL(url).searchParams;
      id = params.get('v') || '';
    }
    return { type: 'youtube', src: `https://www.youtube.com/embed/${id}` };
  }

  // Vimeo variants
  if (u.includes('vimeo.com')) {
    const parts = u.split('/').filter(Boolean);
    const id = parts[parts.length - 1];
    return { type: 'vimeo', src: `https://player.vimeo.com/video/${id}` };
  }

  // Loom variants
  if (u.includes('loom.com')) {
    const parts = u.split('/').filter(Boolean);
    const id = parts[parts.length - 1];
    return { type: 'loom', src: `https://www.loom.com/embed/${id}` };
  }

  // Files: .mp4/.webm
  if (u.endsWith('.mp4') || u.endsWith('.webm')) {
    return { type: 'file', src: url };
  }

  // Fallback to iframe
  return { type: 'loom', src: url };
}

export function DemoEmbedSimple({ title = 'See it in action', videoUrl, description, className }: Props) {
  const embed = toEmbedUrl(videoUrl);
  return (
    <Card title={title} icon={<PlayCircle size={22} className="text-rose-600" />} className={className}>
      <div className="space-y-4">
        <div className="w-full bg-black rounded-xl border-2 border-black overflow-hidden shadow-[4px_4px_0_0_rgb(17,24,39)]">
          {embed.type === 'file' ? (
            <video controls playsInline className="w-full aspect-video">
              <source src={embed.src} type={embed.src.endsWith('.webm') ? 'video/webm' : 'video/mp4'} />
            </video>
          ) : (
            <iframe
              src={embed.src}
              className="w-full aspect-video"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Agent demo"
            />
          )}
        </div>
        {description && <p className="text-sm text-slate-700">{description}</p>}
      </div>
    </Card>
  );
}

export default DemoEmbedSimple;
